/**
 * Device Fingerprint Service
 * Generates a unique browser/device fingerprint for anti-fraud purposes
 */
import FingerprintJS from '@fingerprintjs/fingerprintjs';

// Cache the fingerprint to avoid regenerating on every call
let cachedFingerprint = null;
let fpPromise = null;

/**
 * Initialize FingerprintJS agent
 */
const initAgent = () => {
  if (!fpPromise) {
    fpPromise = FingerprintJS.load();
  }
  return fpPromise;
};

/**
 * Get device fingerprint
 * @returns {Promise<Object>} Fingerprint data including visitorId and components
 */
export const getDeviceFingerprint = async () => {
  try {
    // Return cached fingerprint if available
    if (cachedFingerprint) {
      return cachedFingerprint;
    }

    // Initialize agent and get fingerprint
    const fp = await initAgent();
    const result = await fp.get();

    // Extract useful components for fraud detection
    const fingerprint = {
      visitorId: result.visitorId,
      confidence: result.confidence?.score || 0,
      components: {
        // Browser info
        platform: result.components?.platform?.value || navigator.platform,
        timezone: result.components?.timezone?.value || Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: result.components?.languages?.value?.[0] || navigator.language,
        screenResolution: result.components?.screenResolution?.value?.join('x') || `${window.screen.width}x${window.screen.height}`,
        colorDepth: result.components?.colorDepth?.value || screen.colorDepth,
        
        // Hardware hints
        hardwareConcurrency: result.components?.hardwareConcurrency?.value || navigator.hardwareConcurrency,
        deviceMemory: result.components?.deviceMemory?.value || navigator.deviceMemory,
        
        // Canvas and WebGL (unique rendering)
        canvas: result.components?.canvas?.value ? 'detected' : 'not_detected',
        webgl: result.components?.webgl?.value ? 'detected' : 'not_detected',
        
        // Audio fingerprint
        audio: result.components?.audio?.value ? 'detected' : 'not_detected',
        
        // Fonts (unique to system)
        fontsCount: result.components?.fonts?.value?.length || 0,
        
        // Plugin detection
        plugins: result.components?.plugins?.value?.length || 0,
        
        // Touch support
        touchSupport: result.components?.touchSupport?.value?.maxTouchPoints || 0
      },
      // Additional anti-fraud signals
      signals: {
        incognito: result.components?.incognito?.value || false,
        bot: result.components?.bot?.value || false,
        timestamp: new Date().toISOString()
      }
    };

    // Cache the fingerprint
    cachedFingerprint = fingerprint;

    return fingerprint;
  } catch (error) {
    console.error('Error generating device fingerprint:', error);
    
    // Return a fallback fingerprint using available browser APIs
    return getFallbackFingerprint();
  }
};

/**
 * Fallback fingerprint generation using basic browser APIs
 */
const getFallbackFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('TuPlazaIA Fingerprint', 2, 2);
  const canvasHash = canvas.toDataURL().slice(-50);

  const fallback = {
    visitorId: generateFallbackId(),
    confidence: 0.5,
    components: {
      platform: navigator.platform,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      colorDepth: screen.colorDepth,
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      deviceMemory: navigator.deviceMemory || 0,
      canvas: canvasHash,
      webgl: 'fallback',
      audio: 'fallback',
      fontsCount: 0,
      plugins: navigator.plugins?.length || 0,
      touchSupport: navigator.maxTouchPoints || 0
    },
    signals: {
      incognito: false,
      bot: false,
      timestamp: new Date().toISOString(),
      fallback: true
    }
  };

  cachedFingerprint = fallback;
  return fallback;
};

/**
 * Generate a fallback visitor ID from available data
 */
const generateFallbackId = () => {
  const data = [
    navigator.userAgent,
    navigator.language,
    navigator.platform,
    new Date().getTimezoneOffset(),
    screen.width,
    screen.height,
    screen.colorDepth,
    navigator.hardwareConcurrency || '',
    navigator.deviceMemory || ''
  ].join('|');

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  return 'fb_' + Math.abs(hash).toString(36) + Date.now().toString(36).slice(-4);
};

/**
 * Clear cached fingerprint (useful for testing or when device changes)
 */
export const clearFingerprintCache = () => {
  cachedFingerprint = null;
};

/**
 * Get only the visitor ID (shorter form)
 */
export const getVisitorId = async () => {
  const fp = await getDeviceFingerprint();
  return fp.visitorId;
};

/**
 * Check if device appears to be the same as a previous fingerprint
 */
export const isSameDevice = async (storedFingerprint) => {
  const current = await getDeviceFingerprint();
  
  // Compare visitor IDs
  if (current.visitorId === storedFingerprint.visitorId) {
    return { same: true, confidence: 'high' };
  }
  
  // Check key components for similarity
  const matchingComponents = [
    'platform',
    'timezone',
    'screenResolution',
    'hardwareConcurrency',
    'colorDepth'
  ];
  
  let matches = 0;
  for (const comp of matchingComponents) {
    if (current.components[comp] === storedFingerprint.components?.[comp]) {
      matches++;
    }
  }
  
  const similarity = matches / matchingComponents.length;
  
  if (similarity >= 0.8) {
    return { same: true, confidence: 'medium' };
  } else if (similarity >= 0.6) {
    return { same: 'possible', confidence: 'low' };
  }
  
  return { same: false, confidence: 'high' };
};

export default {
  getDeviceFingerprint,
  getVisitorId,
  clearFingerprintCache,
  isSameDevice
};
